import { useState, useEffect, useRef, ReactNode } from "react";
import { useLocation, Link } from "wouter";
import { Home } from "../pages/Home";
import { Resume } from "../pages/Resume";
import styles from "../styles/main-page.module.css";
import { Contact } from "../pages/Contact";

const pageOrder = ["/resume", "/home", "/contact"] as const;
const pageLabels: Record<string, string> = {
    "/resume": "Resume",
    "/home": "Home",
    "/contact": "Contact/Links",
};

const pageComponents: Record<string, ReactNode> = {
    "/resume": <Resume />,
    "/home": <Home />,
    "/contact": <Contact />,
};

export function PageShell() {
    const [location] = useLocation();
    const [displayedContent, setDisplayedContent] = useState<ReactNode>(
        pageComponents[location] ?? pageComponents["/home"],
    );
    const [fading, setFading] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const boxRef = useRef<HTMLDivElement>(null);
    const prevLocation = useRef(location);

    useEffect(() => {
        if (location === prevLocation.current) return;
        if (!pageComponents[location]) return;

        setFading(true);

        const timeout = setTimeout(() => {
            setDisplayedContent(pageComponents[location]);
            prevLocation.current = location;
            setFading(false);
        }, 300); // fade out duration

        return () => clearTimeout(timeout);
    }, [location]);

    const currentIndex = pageOrder.indexOf(location as any);
    const leftPage =
        pageOrder[(pageOrder.length + currentIndex - 1) % pageOrder.length];
    const rightPage = pageOrder[(currentIndex + 1) % pageOrder.length];

    const isMainPage = location in pageComponents;
    if (!isMainPage) return null;

    return (
        <div className={styles.homePageContainer}>
            <div className={styles.homePageContent}>
                <div className={styles.homeButtonContainer}>
                    {leftPage ? (
                        <Link
                            href={leftPage}
                            style={{ flex: 1, pointerEvents: "all" }}
                        >
                            <button className={styles.homeButton}>
                                <span>← {pageLabels[leftPage]}</span>
                            </button>
                        </Link>
                    ) : (
                        <div style={{ flex: 1 }} />
                    )}

                    <Link
                        href="/projects"
                        style={{ flex: 1, pointerEvents: "all" }}
                    >
                        <button
                            className={`${styles.homeButton} ${styles.portfolioButton}`}
                        >
                            ↑ Portfolio ↑
                        </button>
                    </Link>

                    {rightPage ? (
                        <Link
                            href={rightPage}
                            style={{ flex: 1, pointerEvents: "all" }}
                        >
                            <button className={styles.homeButton}>
                                <span>{pageLabels[rightPage]} →</span>
                            </button>
                        </Link>
                    ) : (
                        <div style={{ flex: 1 }} />
                    )}
                </div>
                <div
                    ref={boxRef}
                    className={styles.homeInfoBox}
                >
                    <div
                        ref={contentRef}
                        className={
                            fading
                                ? styles.contentFadeOut
                                : styles.contentFadeIn
                        }
                    >
                        {displayedContent}
                    </div>
                </div>
            </div>
        </div>
    );
}
